import React, { useCallback, useEffect, useState } from "react";
import api, { brl, fmtDate, rotuloParcela } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Search, BadgeDollarSign, Handshake, FileSignature, MessageCircle } from "lucide-react";
import { ComunicarClienteDialog } from "@/components/ComunicarClienteDialog";
import { BaixaModal } from "@/components/BaixaModal";
import { useRefreshListener } from "@/lib/refresh";
import { HistoricoParcelaDialog } from "@/components/HistoricoParcelaDialog";
import { History } from "lucide-react";
import { Receipt } from "lucide-react";
import { toast } from "sonner";
import { MobileCards } from "@/components/MobileCards";
import { useAuth } from "@/context/AuthContext";
import { useRecurso } from "@/lib/recursos";
import { AcordoModal } from "@/components/AcordoModal";
import { AcordoLinkDialog } from "@/components/AcordoLinkDialog";
import { InstallmentsCalendar } from "@/components/InstallmentsCalendar";
import { List, CalendarDays, CalendarClock } from "lucide-react";
import { AdiarOuAcordoDialog } from "@/components/AdiarOuAcordoDialog";
import { useNavigate } from "react-router-dom";

const Saldo = ({ p }) => {
  const e = p.encargos; const aberto = p.status !== "pago" && p.status !== "renegociado" && p.status !== "cancelado";
  return (
    <div className="min-w-0" data-testid={`inst-valores-${p.id}`}>
      <div className="font-semibold mono">{brl(p.valor)}{p.composicao && <span className="block text-[11px] font-normal text-slate-500">Normal {brl(p.composicao.normal)} + acordo {brl(p.composicao.acordo)}</span>}</div>
      {aberto && e && (e.total_devido !== Number(p.valor) || Number(p.valor_pago) > 0) && <div className="text-xs text-slate-600">Saldo atualizado <b className="mono text-onyx" data-testid={`inst-saldo-${p.id}`}>{brl(e.total_devido)}</b>{(e.juros > 0 || e.multa > 0) && <span className="text-red-600"> (+{brl(e.juros + e.multa)} juros/multa)</span>}{Number(p.valor_pago) > 0 && <span> · pago {brl(p.valor_pago)}</span>}</div>}
      {p.acordo && <span className="inline-block mt-0.5 text-[10px] rounded-full border border-amber-300 bg-amber-50 text-amber-900 px-1.5" data-testid={`inst-acordo-${p.id}`}>{p.acordo.numero || "Acordo"}{p.origem === "acordo_entrada" ? " · Entrada" : p.acordo.modalidade === "incorporado" ? " · Incorporado" : p.origem === "acordo" ? " · Separado" : p.status === "renegociado" ? " · Renegociada" : ""}</span>}
    </div>
  );
};


export default function Installments() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("status") || "todos");
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [acordo, setAcordo] = useState(null);
  const [adiar, setAdiar] = useState(null);
  const [texto, setTexto] = useState(null);
  const [comprovante, setComprovante] = useState(null);
  const [hist, setHist] = useState(null);
  const closeTexto = useCallback(() => setTexto(null), []);
  const [termo, setTermo] = useState(null);
  const [view, setView] = useState("lista");
  const [refreshKey, setRefreshKey] = useState(0);
  const openTermo = async (p) => {
    try {
      const { data } = await api.get(`/acordos?contrato_id=${p.contrato_id}`);
      const a = data.find((x) => x.id === p.acordo_id);
      if (!a) return toast.error("Acordo não encontrado");
      setTermo({ acordo: a, telefone: p.cliente_telefone });
    } catch { toast.error("Erro ao carregar acordo"); }
  };
  const navigate = useNavigate();
  const irAcordo = (p) => navigate(`/acordos/novo?contrato_id=${p.contrato_id}&parcela_id=${p.id}`);
  const { user } = useAuth();
  const temAcordos = useRecurso("acordos");
  const canAcordo = temAcordos && (user?.role === "administrador" || user?.role === "financeiro" || user?.role === "agente_campo");
  const canAdiar = user?.role === "administrador" || user?.role === "financeiro";

  const load = useCallback(async () => {
    setLoading(true);
    const st = tab === "todos" ? "" : tab;
    const { data } = await api.get(`/installments?q=${encodeURIComponent(q)}&status=${st}&encargos=1`);
    setItems(data); setLoading(false);
    setRefreshKey((k) => k + 1);
  }, [q, tab]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useRefreshListener(load);

  return (
    <div className="space-y-5 fade-up" data-testid="installments-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl font-extrabold tracking-tight">Controle de Parcelas</h1><p className="text-slate-500 text-sm">Acompanhe vencimentos e registre pagamentos.</p></div>
        <div className="inline-flex rounded-lg border p-0.5 bg-white">
          <Button size="sm" variant={view === "lista" ? "default" : "ghost"} onClick={() => setView("lista")} className={`gap-1 ${view === "lista" ? "bg-onyx text-white hover:bg-onyx" : ""}`} data-testid="inst-view-lista"><List className="w-4 h-4" /> Lista</Button>
          <Button size="sm" variant={view === "calendario" ? "default" : "ghost"} onClick={() => setView("calendario")} className={`gap-1 ${view === "calendario" ? "bg-onyx text-white hover:bg-onyx" : ""}`} data-testid="inst-view-calendario"><CalendarDays className="w-4 h-4" /> Calendário</Button>
        </div>
      </div>

      {view === "calendario" && <InstallmentsCalendar refreshKey={refreshKey} onBaixa={(p) => setSel(p)} />}

      <div className={`flex gap-3 flex-wrap items-center ${view === "calendario" ? "hidden" : ""}`}>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="todos" data-testid="inst-tab-todos">Todas</TabsTrigger>
            <TabsTrigger value="pendente" data-testid="inst-tab-pendente">A Vencer</TabsTrigger>
            <TabsTrigger value="vencido" data-testid="inst-tab-vencido">Atrasadas</TabsTrigger>
            <TabsTrigger value="parcial" data-testid="inst-tab-parcial">Parciais</TabsTrigger>
            <TabsTrigger value="pago" data-testid="inst-tab-pago">Pagas</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente..." className="pl-9" data-testid="inst-search" />
        </div>
      </div>

      <MobileCards items={items} loading={loading} empty="Nenhuma parcela." testid="inst-cards-mobile" render={(p) => (
        <div key={p.id} className={`trt-card p-4 space-y-3 ${view === "calendario" ? "hidden" : ""}`} data-testid={`inst-card-${p.id}`}>
          <div className="flex items-start justify-between gap-2 min-w-0">
            <div className="min-w-0 flex-1"><div className="font-bold break-words leading-snug">{p.cliente_nome}</div><div className="text-xs text-slate-500">{rotuloParcela(p)} • vence {fmtDate(p.vencimento)}{p.contrato_numero && ` • ${p.contrato_numero}`}</div></div>
            <StatusBadge type="inst" status={p.status} vencimento={p.vencimento} />
          </div>
          <div className="flex items-end justify-between gap-2 flex-wrap">
            <Saldo p={p} />
          </div>
          <div className="flex gap-2 flex-wrap">
            {!["pago", "renegociado", "cancelado"].includes(p.status) && (
              <Button variant="outline" onClick={() => setTexto(p)} data-testid={`texto-card-${p.id}`} className="gap-1 border-emerald-200 text-emerald-700"><MessageCircle className="w-4 h-4" /> Cobrança</Button>
            )}
            {!["pago", "renegociado", "cancelado"].includes(p.status) && (
              <Button onClick={() => setSel(p)} data-testid={`dar-baixa-card-${p.id}`} className="flex-1 bg-gold hover:bg-[hsl(var(--gold-hover))] text-black font-semibold gap-1"><BadgeDollarSign className="w-4 h-4" /> DAR BAIXA</Button>
            )}
            <Button variant="ghost" onClick={() => setHist(p)} data-testid={`historico-card-${p.id}`} className="gap-1 text-slate-600"><History className="w-4 h-4" /> Histórico</Button>
            {(p.status === "pago" || p.status === "parcial") && (
              <Button variant="outline" onClick={() => setComprovante(p)} data-testid={`comprovante-card-${p.id}`} className="flex-1 gap-1 border-emerald-200 text-emerald-700"><Receipt className="w-4 h-4" /> Enviar comprovante</Button>
            )}
            {canAcordo && (p.status === "vencido" || p.status === "parcial") && (
              <Button variant="outline" onClick={() => irAcordo(p)} data-testid={`acordo-card-${p.id}`} className="gap-1 border-violet-200 text-violet-700"><Handshake className="w-4 h-4" /> Acordo</Button>
            )}
            {(canAcordo || canAdiar) && ["pendente", "vencido", "parcial"].includes(p.status) && (
              <Button variant="outline" onClick={() => setAdiar(p)} data-testid={`adiar-card-${p.id}`} className="gap-1 border-sky-200 text-sky-700"><CalendarClock className="w-4 h-4" /> Adiar / Acordo</Button>
            )}
            {p.acordo_id && <Button variant="ghost" onClick={() => openTermo(p)} className="gap-1 text-violet-700"><FileSignature className="w-4 h-4" /> Termo</Button>}
          </div>
        </div>
      )} />
      <div className={`trt-card overflow-x-auto hidden lg:block ${view === "calendario" ? "!hidden" : ""}`}>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr><th className="text-left px-4 py-3">Cliente / contrato</th><th className="text-left px-4 py-3">Parcela</th>
              <th className="text-left px-4 py-3">Vencimento</th><th className="text-left px-4 py-3">Valor / saldo atualizado</th>
              <th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Ações</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin gold-text mx-auto" /></td></tr>
              : items.length === 0 ? <tr><td colSpan={6} className="py-10 text-center text-slate-400">Nenhuma parcela.</td></tr>
              : items.map((p) => (
                <tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`inst-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium max-w-[14rem]"><div className="break-words">{p.cliente_nome}</div><div className="text-xs text-slate-500 mono">{p.contrato_numero}</div></td>
                  <td className="px-4 py-3 whitespace-nowrap">{p.origem === "acordo_entrada" ? "Entrada" : p.origem === "acordo" && p.numero_acordo ? `${p.numero_acordo}${p.acordo_total ? `/${p.acordo_total}` : ""} acordo` : `#${p.numero}`}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(p.vencimento)}</td>
                  <td className="px-4 py-3"><Saldo p={p} /></td>
                  <td className="px-4 py-3"><StatusBadge type="inst" status={p.status} vencimento={p.vencimento} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1 flex-wrap max-w-[24rem] ml-auto" data-testid={`inst-acoes-${p.id}`}>
                      {!["pago", "renegociado", "cancelado"].includes(p.status) && (
                        <Button size="sm" variant="outline" onClick={() => setTexto(p)} data-testid={`texto-btn-${p.id}`}
                          className="gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50" title="Comunicar cliente — cobrança (modelo configurado)">
                          <MessageCircle className="w-4 h-4" /> <span className="hidden xl:inline">COBRANÇA</span>
                        </Button>
                      )}
                      {p.acordo_id && (
                        <Button size="sm" variant="ghost" onClick={() => openTermo(p)} data-testid={`termo-btn-${p.id}`}
                          className="gap-1 text-violet-700 hover:bg-violet-50" title="Termo de acordo / link de assinatura">
                          <FileSignature className="w-4 h-4" /> <span className="hidden xl:inline">TERMO</span>
                        </Button>
                      )}
                      {(canAcordo || canAdiar) && ["pendente", "vencido", "parcial"].includes(p.status) && (
                        <Button size="sm" variant="outline" onClick={() => setAdiar(p)} data-testid={`adiar-btn-${p.id}`}
                          className="gap-1 border-sky-200 text-sky-700 hover:bg-sky-50" title="Adiar vencimento ou realizar acordo">
                          <CalendarClock className="w-4 h-4" /> <span className="hidden xl:inline">ADIAR</span>
                        </Button>
                      )}
                      {canAcordo && (p.status === "vencido" || p.status === "parcial") && (
                        <Button size="sm" variant="outline" onClick={() => irAcordo(p)} data-testid={`acordo-btn-${p.id}`}
                          className="gap-1 border-violet-200 text-violet-700 hover:bg-violet-50" title="Renegociar parcelas atrasadas">
                          <Handshake className="w-4 h-4" /> <span className="hidden xl:inline">ACORDO</span>
                        </Button>
                      )}
                      {!["pago", "renegociado", "cancelado"].includes(p.status) && (
                        <Button size="sm" onClick={() => setSel(p)} data-testid={`dar-baixa-btn-${p.id}`}
                          className="bg-gold hover:bg-[hsl(var(--gold-hover))] text-black font-semibold gap-1">
                          <BadgeDollarSign className="w-4 h-4" /> <span className="hidden xl:inline">DAR </span>BAIXA
                        </Button>
                      )}
                      {p.status !== "renegociado" && (
                        <Button size="sm" variant="ghost" onClick={() => setHist(p)} data-testid={`historico-btn-${p.id}`} className="gap-1 text-slate-600" title="Histórico de pagamentos da parcela">
                          <History className="w-4 h-4" />
                        </Button>
                      )}
                      {(p.status === "pago" || p.status === "parcial") && (
                        <Button size="sm" variant="outline" onClick={() => setComprovante(p)} data-testid={`comprovante-btn-${p.id}`}
                          className="gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50" title="Enviar comprovante de pagamento (texto ou PDF)">
                          <Receipt className="w-4 h-4" /> <span className="hidden xl:inline">COMPROVANTE</span>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <BaixaModal inst={sel} onClose={() => setSel(null)} onDone={load} />
      <AdiarOuAcordoDialog inst={adiar} onClose={() => setAdiar(null)} onDone={load} onAcordo={irAcordo} />
      <AcordoModal inst={acordo} onClose={() => setAcordo(null)} onDone={load} onCreated={(a) => setTermo({ acordo: a, telefone: acordo?.cliente_telefone })} />
      {texto && <ComunicarClienteDialog contexto="parcela" parcelaId={texto.id} onClose={closeTexto} />}
      {comprovante && <ComunicarClienteDialog contexto="parcela_paga" parcelaId={comprovante.id} onClose={() => setComprovante(null)} />}
      {hist && <HistoricoParcelaDialog installmentId={hist.id} onClose={() => setHist(null)} onChanged={load} />}
      {termo && <AcordoLinkDialog acordo={termo.acordo} telefone={termo.telefone} onClose={() => setTermo(null)} />}
    </div>
  );
}

export { BaixaModal };

